import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { exportDossier, ExportFormat } from '../../services/dossierExportService';
import DocumentText from '../icons/DocumentText';

interface ExportDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    dossierId: string;
}

interface ExportOption {
    format: ExportFormat;
    label: string;
    description: string;
    badge: string;
    badgeColor: string;
}

const exportOptions: ExportOption[] = [
    {
        format: 'pdf',
        label: 'PDF',
        description: 'Document PDF',
        badge: 'PDF',
        badgeColor: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    },
    {
        format: 'docx',
        label: 'Word',
        description: 'Document Word (.docx)',
        badge: 'DOC',
        badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    },
    {
        format: 'md',
        label: 'Markdown',
        description: 'Fichier Markdown (.md)',
        badge: 'MD',
        badgeColor: 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-300',
    },
];

const ExportDropdown: React.FC<ExportDropdownProps> = ({
    isOpen,
    onClose,
    dossierId,
}) => {
    const [loadingFormat, setLoadingFormat] = useState<ExportFormat | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleExport = async (format: ExportFormat) => {
        if (loadingFormat) return;

        setLoadingFormat(format);
        setError(null);

        const success = await exportDossier(dossierId, format);

        if (!success) {
            setError('Échec de l\'export. Veuillez réessayer.');
        } else {
            onClose();
        }

        setLoadingFormat(null);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 w-64 bg-white dark:bg-mcdm-third-500 rounded-lg shadow-lg z-50 overflow-hidden"
            >
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-mcdm-third-400">
                    <h3 className="text-sm font-medium text-mcdm-third-500 dark:text-white flex items-center gap-2">
                        <DocumentText width="16px" height="16px" />
                        Exporter le dossier
                    </h3>
                </div>

                {/* Error */}
                {error && (
                    <div className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs">
                        {error}
                    </div>
                )}

                {/* Options */}
                <div className="py-1">
                    {exportOptions.map((option) => (
                        <button
                            key={option.format}
                            onClick={() => handleExport(option.format)}
                            disabled={loadingFormat !== null}
                            className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                                loadingFormat === option.format
                                    ? 'bg-mcdm-primary-200 dark:bg-mcdm-third-400 cursor-wait'
                                    : 'hover:bg-gray-50 dark:hover:bg-mcdm-third-400 cursor-pointer'
                            } ${
                                loadingFormat !== null && loadingFormat !== option.format
                                    ? 'opacity-50'
                                    : ''
                            }`}
                        >
                            <span className={`text-[10px] font-bold flex-shrink-0 w-8 h-8 rounded flex items-center justify-center ${option.badgeColor}`}>{option.badge}</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-mcdm-third-500 dark:text-white">
                                    {option.label}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {option.description}
                                </div>
                            </div>
                            {loadingFormat === option.format && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-mcdm-secondary-400 flex-shrink-0" />
                            )}
                        </button>
                    ))}
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ExportDropdown;
